import { RefCallback, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

interface WindowedRowsOptions {
  // Number of items in the list.
  count: number;
  // Fixed row height in px. Ignored when `itemHeights` is provided.
  rowHeight?: number;
  // Per-item heights in px for variable-height lists (e.g. a header row plus a
  // varying number of detail rows). Takes precedence over `rowHeight`.
  itemHeights?: number[];
  // Extra pixels rendered above/below the viewport to avoid blank gaps.
  overscanPx?: number;
}

interface WindowedRows {
  // Attach to the element that wraps the rendered rows.
  contentRef: RefCallback<HTMLDivElement>;
  // Render `items.slice(startIndex, endIndex)`; the item's real index is
  // `startIndex + offset`.
  startIndex: number;
  endIndex: number;
  // Spacer heights (px) that stand in for the off-screen rows so the scrollbar
  // stays correct. Render a zero-size `<div>` with these heights around the rows.
  topSpacer: number;
  bottomSpacer: number;
}

// Nearest scrollable ancestor (including the element itself), or null if the
// page/window is what scrolls. The various tables that use this hook don't pin
// their scroll container with a single CSS rule, so we resolve it at runtime.
const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el: HTMLElement | null = node;
  while (el && el !== document.body && el !== document.documentElement) {
    const overflowY = window.getComputedStyle(el).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

/**
 * Renders only the rows currently in (or near) the viewport for a long list,
 * keeping the scroll height intact via spacer elements. Works whether the list
 * itself scrolls, an ancestor scrolls, or the page scrolls.
 */
export const useWindowedRows = ({
  count,
  rowHeight = 0,
  itemHeights,
  overscanPx = 400,
}: WindowedRowsOptions): WindowedRows => {
  const contentElRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  // Viewport geometry expressed in the scroll container's content coordinates.
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentOffset, setContentOffset] = useState(0);

  const measure = useCallback(() => {
    const content = contentElRef.current;
    if (!content) return;
    const scroller = scrollerRef.current;

    if (!scroller) {
      // Page/window scrolls: content top lives in document coordinates.
      const se = document.scrollingElement ?? document.documentElement;
      const rect = content.getBoundingClientRect();
      setContentOffset(rect.top + se.scrollTop);
      setScrollTop(se.scrollTop);
      setViewportHeight(window.innerHeight);
      return;
    }

    if (scroller === content) {
      setContentOffset(0);
    } else {
      const rect = content.getBoundingClientRect();
      const sRect = scroller.getBoundingClientRect();
      setContentOffset(rect.top - sRect.top + scroller.scrollTop);
    }
    setScrollTop(scroller.scrollTop);
    setViewportHeight(scroller.clientHeight);
  }, []);

  // A callback ref lets us (re)resolve the scroll container and (de)register
  // listeners exactly when the rows element mounts or unmounts.
  const contentRef = useCallback<RefCallback<HTMLDivElement>>((node) => {
    cleanupRef.current?.();
    cleanupRef.current = undefined;
    contentElRef.current = node;
    if (!node) return;

    scrollerRef.current = getScrollParent(node);
    measure();

    // Capture-phase scroll catches whichever ancestor actually scrolls.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (scrollerRef.current) observer.observe(scrollerRef.current);

    cleanupRef.current = () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [measure]);

  // On mount a short list may not overflow yet, so the scroll container is
  // unknown; re-resolve it (and re-measure) when the item count changes.
  useLayoutEffect(() => {
    if (contentElRef.current && !scrollerRef.current) {
      scrollerRef.current = getScrollParent(contentElRef.current);
    }
    measure();
  }, [count, measure]);

  // Cumulative top offset of each item (variable-height mode only).
  const offsets = useMemo(() => {
    if (!itemHeights) return null;
    const offs = new Array<number>(itemHeights.length + 1);
    offs[0] = 0;
    for (let i = 0; i < itemHeights.length; i++) {
      offs[i + 1] = offs[i] + itemHeights[i];
    }
    return offs;
  }, [itemHeights]);

  const totalHeight = offsets ? offsets[count] : count * rowHeight;
  const viewTop = scrollTop - contentOffset - overscanPx;
  const viewBottom = scrollTop - contentOffset + viewportHeight + overscanPx;

  let startIndex: number;
  let endIndex: number;
  if (offsets) {
    startIndex = 0;
    while (startIndex < count && offsets[startIndex + 1] <= viewTop) startIndex++;
    endIndex = startIndex;
    while (endIndex < count && offsets[endIndex] < viewBottom) endIndex++;
  } else {
    startIndex = Math.max(0, Math.floor(viewTop / rowHeight));
    endIndex = Math.min(count, Math.ceil(viewBottom / rowHeight));
  }

  const topSpacer = offsets ? offsets[startIndex] : startIndex * rowHeight;
  const bottomSpacer = Math.max(
    0,
    totalHeight - (offsets ? offsets[endIndex] : endIndex * rowHeight)
  );

  return { contentRef, startIndex, endIndex, topSpacer, bottomSpacer };
};
